<div class="vflex-300 content-container">
  <h2>Menus</h2>
  <div class="surface-muted border-radius-200 pad-200">
    <p>
      <code>import &#123; CdkMenuItem &#125; from '&#64;angular/cdk/menu';</code>
    </p>
    <p>
      <code>
        import &#123; MenuComponent, MenutriggerDirective &#125; from
        '&#64;spaces-ui/meadow/components/menu';
      </code>
    </p>
  </div>
  <p>
    Menus come from the
    <a href="https://material.angular.io/cdk/menu/overview">CDK Menu</a>
    . This gives us built-in ARIA support as well as keyboard interaction, and of course most of the
    implementation has already been developed and tested.
  </p>
  <p>This page shows a couple of examples of how to use the CDK Menu in Meadow.</p>
  <p>
    The list item styling comes from the meadow listitem component, so no additional code is
    required to make it look and behave as expected.
  </p>

  <ng-template #inlineMenu data-sample="inlineMenu">
    <div class="surface-flat-outline border-radius-200 pad-100" cdkMenu>
      <menu>
        <ul>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 1 clicked')" [icon]="icons.Logout">
            Logout
          </li>
          <li
            cdkMenuItem
            (cdkMenuItemTriggered)="alert('Item 2 clicked')"
            [icon]="icons.Mark"
            [cdkMenuItemDisabled]="true"
          >
            Create Space
          </li>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 3 clicked')" [icon]="icons.Channel">
            Create Channel
          </li>
        </ul>
      </menu>
    </div>
  </ng-template>
  <app-sample [sampleContent]="inlineMenu" sampleName="inlineMenu"></app-sample>

  <h3>Flyout Menu</h3>
  <p>
    Both menu and nav list items can have a sub-header and a divider. The sub-header is a label that
    is styled differently from the list items. The divider is a horizontal line that separates
    groups of items.
  </p>
  <ng-template #flyoutMenu data-sample="flyoutMenu">
    <div>
      <button [cdkMenuTriggerFor]="menu" class="mdw-button mdw-primary">Trigger Flyout!</button>
    </div>
    <ng-template #menu>
      <menu
        class="pad-100 surface-elevated border-radius-100"
        cdkMenu
        animate.enter="menu-enter"
        animate.leave="menu-leave"
      >
        <label>Some Menu Items</label>
        <ul>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 1 clicked')" [icon]="icons.Logout">
            Logout
          </li>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 2 clicked')" [icon]="icons.Mark">
            Create Space
          </li>
          <hr />
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 3 clicked')" [icon]="icons.Channel">
            Create Channel
          </li>
        </ul>
      </menu>
    </ng-template>
  </ng-template>
  <app-sample [sampleContent]="flyoutMenu" sampleName="flyoutMenu"></app-sample>

  <h3>Nested Flyout Menu</h3>
  <ng-template #nestedFlyoutMenu data-sample="nestedFlyoutMenu">
    <div>
      <button [cdkMenuTriggerFor]="menu" class="mdw-button mdw-primary">Trigger Flyout!</button>
    </div>
    <ng-template #menu>
      <menu
        class="pad-100 surface-elevated border-radius-100"
        cdkMenu
        mdwMenuAim
        animate.enter="menu-enter"
        animate.leave="menu-leave"
      >
        <label>Some Menu Items</label>
        <ul>
          <li
            cdkMenuItem
            [cdkMenuTriggerFor]="fileMenu"
            (cdkMenuItemTriggered)="alert('Item 1 clicked')"
            [icon]="icons.Logout"
          >
            File
          </li>
          <li
            cdkMenuItem
            [cdkMenuTriggerFor]="settingsMenu"
            (cdkMenuItemTriggered)="alert('Item 2 clicked')"
            [icon]="icons.Mark"
          >
            Settings
          </li>
          <hr />
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Item 3 clicked')" [icon]="icons.Channel">
            Help
          </li>
        </ul>
      </menu>
    </ng-template>

    <ng-template #fileMenu>
      <menu
        class="pad-100 surface-elevated border-radius-100"
        cdkMenu
        animate.enter="menu-enter"
        animate.leave="menu-leave"
      >
        <label>File Options</label>
        <ul>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('New File clicked')" [icon]="icons.Plus">
            New File
          </li>
          <li
            cdkMenuItem
            (cdkMenuItemTriggered)="alert('Open File clicked')"
            [icon]="icons.Folder_Fill"
          >
            Open File
          </li>
          <li cdkMenuItem (cdkMenuItemTriggered)="alert('Save File clicked')" [icon]="icons.Save">
            Save File
          </li>
        </ul>
      </menu>
    </ng-template>
    <ng-template #settingsMenu>
      <menu
        class="pad-100 surface-elevated border-radius-100"
        cdkMenu
        animate.enter="menu-enter"
        animate.leave="menu-leave"
      >
        <label>Settings Options</label>
        <ul>
          <li
            cdkMenuItem
            (cdkMenuItemTriggered)="alert('Preferences clicked')"
            [icon]="icons.Settings_Filled"
          >
            Preferences
          </li>
          <li
            cdkMenuItem
            (cdkMenuItemTriggered)="alert('Account clicked')"
            [icon]="icons.User_Fill"
          >
            Account
          </li>
          <li
            cdkMenuItem
            (cdkMenuItemTriggered)="alert('Privacy clicked')"
            [icon]="icons.Security_Fill"
          >
            Privacy
          </li>
        </ul>
      </menu>
    </ng-template>
  </ng-template>
  <app-sample [sampleContent]="nestedFlyoutMenu" sampleName="nestedFlyoutMenu"></app-sample>

  <h3>Menu Placement - Connected Position</h3>
  <div class="hflex-200 flex-align-items-end">
    <mdw-select
      label="Origin X"
      description="This is used to designate the X position of the trigger that connects to the flyout"
      [options]="['start', 'center', 'end']"
      [ngModel]="originX()"
      (ngModelChange)="originX.set($event)"
    ></mdw-select>
    <mdw-select
      label="Origin Y"
      description="This is used to designate the Y position of the trigger that connects to the flyout"
      [options]="['top', 'center', 'bottom']"
      [ngModel]="originY()"
      (ngModelChange)="originY.set($event)"
    ></mdw-select>
    <mdw-select
      label="Overlay X"
      description="This is used to designate the X position of the flyout that connects to the trigger"
      [options]="['start', 'center', 'end']"
      [ngModel]="overlayX()"
      (ngModelChange)="overlayX.set($event)"
    ></mdw-select>
    <mdw-select
      label="Overlay Y"
      description="This is used to designate the Y position of the flyout that connects to the trigger"
      [options]="['top', 'center', 'bottom']"
      [ngModel]="overlayY()"
      (ngModelChange)="overlayY.set($event)"
    ></mdw-select>
    <div>
      <input
        label="Offset X"
        description="This is used to adjust the X position of the flyout that connects to the trigger"
        type="text"
        placeholder="offset X"
        [ngModel]="offsetX()"
        (ngModelChange)="offsetX.set($event)"
      />
    </div>
    <div>
      <input
        label="Offset Y"
        description="This is used to adjust the Y position of the flyout that connects to the trigger"
        type="text"
        placeholder="offset Y"
        [ngModel]="offsetY()"
        (ngModelChange)="offsetY.set($event)"
      />
    </div>
  </div>

  @if (showPlacementTrigger()) {
    <div>
      <button
        [cdkMenuTriggerFor]="positioningMenu"
        [cdkMenuPosition]="[connectedPosition()]"
        class="mdw-button mdw-primary"
      >
        Flyout Trigger
      </button>
    </div>
    <ng-template #positioningMenu>
      <menu
        class="pad-100 surface-elevated border-radius-100"
        cdkMenu
        animate.enter="menu-enter"
        animate.leave="menu-leave"
      >
        <menu>
          <ul>
            <li cdkMenuItem>Item 1</li>
            <li cdkMenuItem>Item 2</li>
            <hr />
            <li cdkMenuItem>Item 3</li>
          </ul>
        </menu>
      </menu>
    </ng-template>

    <p class="bold">Trigger Bindings:</p>
    <div class="surface-muted border-radius-200 pad-200">
      <code>
        {{ bindingsString() }}
      </code>
    </div>
  }
</div>
